import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../utils/axios.js"; 
import { AuthContext } from "../../../context/authContext";

export const useNotification = (isNotificationOpen, setNotificationOpen, closeUserDropdown) => {
    const { currentUser } = useContext(AuthContext);
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        if (!currentUser) return;
        makeRequest.get("/notifications/unread-count")
            .then(res => setUnreadCount(res.data.count))
            .catch(err => console.log("Error retrieving unread count", err));
    }, [currentUser]);

    useEffect(() => {
        if (isNotificationOpen) {
            setLoading(true);
            makeRequest.get("/notifications")
                .then(res => {
                    setNotifications(res.data.data);
                    setError(null);
                })
                .catch(err => {
                    setError("Unable to load notification.");
                    console.log(err);
                })
                .finally(() => setLoading(false));
        }
    }, [isNotificationOpen]);

    const handleToggle = () => {
        closeUserDropdown();

        if (!isNotificationOpen && unreadCount > 0) {
            makeRequest.put("/notifications/mark-all-read")
                .then(() => setUnreadCount(0))
                .catch(err => console.log("Error: Mark all read", err));
        }
        
        setNotificationOpen(prev => !prev);
    };

    const handleItemClick = async (noti) => {
        setNotificationOpen(false);

        if (noti.postId && noti.postId !== "null") {
            navigate(`/post/${noti.postId}`);
        } else if (noti.actorId?.id) {
            navigate(`/profile/${noti.actorId.id}`);
        }

        if (!noti.isRead) {
            try {
                await makeRequest.put(`/notifications/mark-read/${noti._id}`);
                setNotifications(prev => prev.map(n => n._id === noti._id ? { ...n, isRead: true } : n));
                setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
            } catch (err) {
                console.log("Mark read item error", err);
            }
        }
    };

    const handleAcceptFriend = async (noti) => {
        try {
            await makeRequest.put("/friendships/confirm", { userId: noti.actorId.id });
            
            if (!noti.isRead) {
                await makeRequest.put(`/notifications/mark-read/${noti._id}`);
                setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
            }
            
            setNotifications(prev => prev.filter(n => n._id !== noti._id));
        } catch (err) {
            console.log("Friend request confirmation error:", err);
        }
    };

    const handleRejectFriend = async (noti) => {
        try {
            await makeRequest.delete(`/friendships?userId=${noti.actorId.id}`);
            
            if (!noti.isRead) {
                await makeRequest.put(`/notifications/mark-read/${noti._id}`);
                setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
            }
            
            setNotifications(prev => prev.filter(n => n._id !== noti._id));
        } catch (err) {
            console.log("Friend request rejection error:", err);
        }
    };

    return {
        notifications,
        loading,
        error,
        unreadCount,
        handleToggle,
        handleItemClick,
        handleAcceptFriend,
        handleRejectFriend
    };
};